import CategoryModel from '../../../../../models/v2/category/category.model';

export const buildCategoryTree = (flatCategories: CategoryModel[]): CategoryModel[] => {
	const uniqueCategories = Array.from(new Map(flatCategories.map((cat) => [cat.id, cat])).values());

	const categoryMap = new Map<string, CategoryModel>();
	const addedToParent = new Set<string>();

	uniqueCategories.forEach((category) => {
		const categoryCopy = Object.assign(new CategoryModel(), {
			...category,
			subs: [],
		});
		categoryMap.set(category.id, categoryCopy);
	});

	const rootCategories: CategoryModel[] = [];

	uniqueCategories.forEach((category) => {
		const categoryWithSubs = categoryMap.get(category.id);
		if (!categoryWithSubs) {
			return;
		}

		if (category.parentId && categoryMap.has(category.parentId)) {
			const parent = categoryMap.get(category.parentId);
			if (parent && !addedToParent.has(category.id)) {
				parent.subs.push(categoryWithSubs);
				addedToParent.add(category.id);
			}
		} else {
			if (!addedToParent.has(category.id)) {
				rootCategories.push(categoryWithSubs);
			}
		}
	});

	return rootCategories;
};

export const buildCategoryTreeClean = (flatCategories: CategoryModel[]): CategoryModel[] => {
	const categoryMap = new Map<string, CategoryModel>();
	const rootCategories: CategoryModel[] = [];

	flatCategories.forEach((category) => {
		categoryMap.set(category.id, {
			...category,
			subs: [],
		});
	});

	categoryMap.forEach((category) => {
		if (category.parentId) {
			const parent = categoryMap.get(category.parentId);
			if (parent) {
				parent.subs.push(category);
			} else {
				rootCategories.push(category);
			}
		} else {
			rootCategories.push(category);
		}
	});

	return rootCategories;
};

export const filterCategories = (hierarchicalCategories: CategoryModel[], searchText: string): CategoryModel[] => {
	if (!searchText.trim()) {
		return hierarchicalCategories;
	}

	const searchLower = searchText.toLowerCase();
	const resultIds = new Set<string>();

	const findMatchingCategories = (categoryList: CategoryModel[]): CategoryModel[] => {
		const results: CategoryModel[] = [];

		categoryList.forEach((category) => {
			if (category.title.toLowerCase().includes(searchLower)) {
				resultIds.add(category.id);
				results.push(category);
			}

			if (category.subs.length > 0) {
				const matchingChildren = findMatchingCategories(category.subs);
				if (matchingChildren.length > 0) {
					resultIds.add(category.id);
					if (!results.some((c) => c.id === category.id)) {
						results.push(category);
					}
				}
			}
		});

		return results;
	};

	const matches = findMatchingCategories(hierarchicalCategories);

	const processHierarchy = (categoryList: CategoryModel[]): CategoryModel[] => {
		return categoryList
			.map((category) => {
				if (!resultIds.has(category.id)) return null;

				const newCategory = { ...category };

				if (category.subs.length > 0) {
					newCategory.subs = category.subs
						.filter((sub) => resultIds.has(sub.id))
						.map((sub) => ({
							...sub,
							subs: sub.subs.length > 0 ? processHierarchy(sub.subs) : [],
						}));
				}

				return newCategory;
			})
			.filter(Boolean) as CategoryModel[];
	};

	return processHierarchy(matches);
};

export const flattenCategories = (categoryList: CategoryModel[]): CategoryModel[] => {
	let flat: CategoryModel[] = [];

	categoryList.forEach((category) => {
		flat.push(category);
		if (category && category.subs && category.subs.length > 0) {
			flat = flat.concat(flattenCategories(category.subs));
		}
	});

	return flat;
};

export const findCategoryById = (categoryList: CategoryModel[], id: string): CategoryModel | null => {
	let result: CategoryModel | null = null;

	categoryList.forEach((category) => {
		if (result) return;

		if (category.id === id) {
			result = category;
			return;
		}

		if (category.subs.length > 0) {
			const found = findCategoryById(category.subs, id);
			if (found) {
				result = found;
				return;
			}
		}
	});

	return result;
};
